import { useEffect, useState } from "react";
import { CheckCircle2, Circle, CloudUpload, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Problem, SslBadge, detail, field, primary } from "./ui";

const STEPS = [["package", "Hosting package"], ["ftp", "FTP unlock"], ["upload", "Upload files"], ["ssl", "Free SSL request"]];

const DnsTable = ({ rows, note }) => (
  <div data-testid="publish-dns-records" className="mt-3 rounded-xl border border-white/10 p-3 text-xs text-slate-300">
    <p className="mb-2 text-slate-400">{note}</p>
    <table className="w-full text-left"><thead className="text-slate-500"><tr><th>Type</th><th>Host</th><th>Value</th><th>TTL</th></tr></thead>
      <tbody>{rows.map((r) => <tr key={r.type + r.host} data-testid={`publish-dns-${r.type}-${r.host}`}><td>{r.type}</td><td>{r.host}</td><td className="font-mono">{r.value}</td><td>{r.ttl}</td></tr>)}</tbody>
    </table>
  </div>
);

const Result = ({ st }) => (
  <div data-testid="publish-state" className="mt-4 space-y-3 text-sm">
    <ol className="grid gap-2 sm:grid-cols-4">
      {STEPS.map(([k, label]) => (
        <li key={k} data-testid={`publish-step-${k}`} className="flex items-center gap-2 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2 text-slate-200">
          {st.steps?.[k]?.done ? <CheckCircle2 className="h-4 w-4 text-emerald-300" /> : <Circle className="h-4 w-4 text-slate-500" />}{label}
        </li>
      ))}
    </ol>
    {st.error && <p data-testid="publish-error-saved" className="text-amber-200">Last attempt stopped: {st.error} — press Publish again to resume.</p>}
    {st.invoice?.invoice && <p data-testid="publish-invoice" className="text-slate-300">Hosting setup draft invoice: ${st.invoice.invoice.total.toFixed(2)} ({st.invoice.invoice.status}){st.invoice.invoice.flag ? ` — ${st.invoice.invoice.flag}` : ""} · review it in Payments</p>}
    {st.invoice?.invoice_note && <p data-testid="publish-invoice-note" className="text-slate-400">{st.invoice.invoice_note}</p>}
    {st.package_id && (
      <div className="flex flex-wrap items-center gap-3">
        <SslBadge status={st.ssl?.status} testId="publish-ssl-status" />
        {st.temporary_url && <a href={st.temporary_url} target="_blank" rel="noopener noreferrer" data-testid="publish-temp-url" className="inline-flex items-center gap-1 text-cyan-200 underline">Temporary URL <ExternalLink className="h-3.5 w-3.5" /></a>}
        <a href={st.live_url} target="_blank" rel="noopener noreferrer" data-testid="publish-live-url" className="inline-flex items-center gap-1 text-cyan-200 underline">{st.live_url} <ExternalLink className="h-3.5 w-3.5" /></a>
      </div>
    )}
    {st.ssl?.status && st.ssl.status !== "Active" && <p data-testid="publish-ssl-fallback" className="text-xs text-slate-400">{st.ssl.note ? `${st.ssl.note}. ` : ""}If it doesn't activate: {st.ssl.fallback}</p>}
    {st.dns_records?.length > 0 && <DnsTable rows={st.dns_records} note={st.dns_note} />}
  </div>
);

export const PublishPanel = ({ projectId, versions = [] }) => {
  const [st, setSt] = useState(null);
  const [domain, setDomain] = useState("");
  const [version, setVersion] = useState("");
  const [busy, setBusy] = useState(false);
  const [fail, setFail] = useState(null);
  useEffect(() => {
    if (!projectId) return;
    setSt(null); setFail(null);
    api.get(`/admin/twentyi/publish/${projectId}`).then((r) => { setSt(r.data); if (r.data.domain) setDomain(r.data.domain); }).catch((e) => setFail(detail(e)));
  }, [projectId]);
  useEffect(() => { if (versions.length) setVersion(String(versions[versions.length - 1].number)); }, [versions]);
  const full = st?.usage && st.usage.used >= st.usage.limit && !st.package_id;
  const publish = async () => {
    setBusy(true); setFail(null);
    try { const r = await api.post("/admin/twentyi/publish", { project_id: projectId, version: Number(version), domain }); setSt(r.data); toast.success("Published to 20i"); }
    catch (e) { setFail(detail(e)); api.get(`/admin/twentyi/publish/${projectId}`).then((r) => setSt(r.data)).catch(() => {}); }
    setBusy(false);
  };
  return (
    <div data-testid="publish-panel">
      <p className="text-sm text-slate-400">Creates a Linux Unlimited package for the domain (or reuses this project's package), uploads the chosen version and requests free SSL. Safe to retry — finished steps are skipped and no second package is created.</p>
      <p className="mt-2 text-xs text-slate-400">Hosting plan: <span data-testid="publish-usage" className="font-semibold text-white">{st?.usage?.label || "…"}</span></p>
      <div className="mt-3 grid gap-3 sm:grid-cols-[2fr_1fr_auto]">
        <input value={domain} onChange={(e) => setDomain(e.target.value.trim().toLowerCase())} disabled={!!st?.package_id} placeholder="clientdomain.com" data-testid="publish-domain-input" className={field} />
        <select value={version} onChange={(e) => setVersion(e.target.value)} data-testid="publish-version-select" className={field}>
          {!versions.length && <option value="">No versions</option>}
          {versions.map((v) => <option key={v.number} value={v.number}>v{v.number}</option>)}
        </select>
        <button onClick={publish} disabled={busy || !domain || !version || full} data-testid="publish-submit-btn" className={primary}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CloudUpload className="h-4 w-4" />}{st?.package_id ? "Republish" : "Publish to 20i"}
        </button>
      </div>
      {full && <p data-testid="publish-limit-reached" className="mt-2 text-sm text-amber-200">All {st.usage.limit} hosting packages are used — free one in My20i before publishing a new site.</p>}
      <Problem d={fail} testId="publish-error" />
      {st && <Result st={st} />}
    </div>
  );
};
